import { Injectable, computed, inject, signal } from '@angular/core';
import { Store } from '@ngrx/store';

import { AppState } from 'src/app/core/state/app.state';
import { selectUser } from 'src/app/core/state';
import { UserApiService } from 'src/app/api/services/user-api.service';
import { NomObjectLifecycleStatus } from 'src/app/core/constants';

/** What opens after login: the Home overview or the Workspaces section. */
export type HomeLandingChoice = 'home' | 'workspaces';

const CONTEXT_KEY = 'home_landing';

function isChoice(value: unknown): value is HomeLandingChoice {
  return value === 'home' || value === 'workspaces';
}

/**
 * The user's answer to the one-time "try the new Home?" question, persisted as
 * the user context value `home_landing` (`{ choice, decided_at }`), one row per
 * user in `user_context_settings`, so the answers can be counted later.
 */
@Injectable({ providedIn: 'root' })
export class HomeLandingService {
  private readonly store = inject(Store<AppState>);
  private readonly userApi = inject(UserApiService);

  private readonly user = this.store.selectSignal(selectUser);
  private readonly change = signal<HomeLandingChoice | null>(null);

  /** The stored choice, null while the user has not answered yet. */
  readonly choice = computed<HomeLandingChoice | null>(() => {
    const stored = this.user()?.context_settings?.[CONTEXT_KEY]?.choice;
    return this.change() ?? (isChoice(stored) ? stored : null);
  });

  readonly decided = computed(() => this.choice() !== null);

  /** The analytics child route that opens by default: Home until the user chose Workspaces. */
  readonly landingPath = computed(() =>
    this.choice() === 'workspaces' ? 'workspaces' : 'home',
  );

  set(choice: HomeLandingChoice): void {
    this.change.set(choice);
    this.userApi
      .saveContextValue({
        contextKey: CONTEXT_KEY,
        contextValue: { choice, decided_at: new Date().toISOString() },
        lifeCycleStatus: [NomObjectLifecycleStatus.ACTIVE],
      })
      .subscribe({
        error: (error) =>
          console.error('Failed to persist the home landing choice', error),
      });
  }
}
